import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  MerchantOrderStatusEnum,
  OpsOrder,
} from '@/graphql/generatedTypes';

import {
  OrderToolbar,
  type FilterDefinition,
  type ActiveFilter,
  type ToolbarAction,
} from './OrderToolbar';
import { OrderPagination } from './pagination';
import { OrderMutationsProvider } from './OrderMutationsContext';
import i18n, { formatNumber } from '@/utils/i18n';
import { ContainerTypeComboboxProps } from './ContainerTypeDropdown';

function shouldRenderCellLink(columnDef: ColumnDef<OpsOrder>) {
  return !(columnDef.meta as { disableRowNavigation?: boolean } | undefined)
    ?.disableRowNavigation;
}

interface OrderDataTableProps {
  columns: ColumnDef<OpsOrder>[];
  data: OpsOrder[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  containerTypes: ContainerTypeComboboxProps['containerTypes'];
  selectedContainerTypes: ContainerTypeComboboxProps['containerTypes'];
  onContainerTypeChange: (
    cts: ContainerTypeComboboxProps['containerTypes'],
  ) => void;
  availableFilters: FilterDefinition[];
  activeFilters: ActiveFilter[];
  onFilterChange: (filterId: string, value: string) => void;
  onFilterAdd: (filterId: string) => void;
  onFilterRemove: (filterId: string) => void;
  getRowHref?: (row: OpsOrder) => string;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (
    updater: VisibilityState | ((prev: VisibilityState) => VisibilityState),
  ) => void;
  primaryAction?: ToolbarAction;
  overflowActions?: ToolbarAction[];
  firstRowFilterIds?: string[];
  onCancel: (ids: string[]) => Promise<void>;
  onUpdateStatus: (
    ids: string[],
    status: MerchantOrderStatusEnum,
  ) => Promise<void>;
  onRefetchOrderDocuments?: (ids: string[]) => Promise<void>;
  onRefetchDeliveryNote?: (ids: string[]) => Promise<void>;
  header?: React.ReactNode;
  isError?: boolean;
}

export function OrderDataTable({
  columns,
  data,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  isLoading,
  sorting,
  onSortingChange,
  containerTypes,
  selectedContainerTypes,
  onContainerTypeChange,
  availableFilters,
  activeFilters,
  onFilterChange,
  onFilterAdd,
  onFilterRemove,
  getRowHref,
  header,
  columnVisibility,
  onColumnVisibilityChange,
  primaryAction,
  overflowActions,
  firstRowFilterIds,
  onCancel,
  onUpdateStatus,
  onRefetchOrderDocuments,
  onRefetchDeliveryNote,
  isError,
}: OrderDataTableProps) {
  'use no memo';
  const [rowSelection, setRowSelection] = React.useState({});
  const [allSelected, setAllSelected] = React.useState(false);
  const selectingAllRef = React.useRef(false);

  React.useEffect(() => {
    setRowSelection({});
    setAllSelected(false);
  }, [activeFilters, selectedContainerTypes]);

  const pageCount = Math.ceil(totalCount / pageSize);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
    enableRowSelection: true,
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setAllSelected(
        selectingAllRef.current || Object.keys(next).length >= totalCount,
      );
      setRowSelection(next);
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange(next);
    },
    onColumnVisibilityChange: onColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    pageCount,
    getRowId: (row) => row.id,
  });

  const handleSelectAll = React.useCallback(() => {
    selectingAllRef.current = true;
    setAllSelected(true);
    table.toggleAllPageRowsSelected(true);
    selectingAllRef.current = false;
  }, [table]);

  const mutationHandlers = React.useMemo(
    () => ({
      onCancel,
      onUpdateStatus,
      onRefetchOrderDocuments,
      onRefetchDeliveryNote,
    }),
    [onCancel, onUpdateStatus, onRefetchOrderDocuments, onRefetchDeliveryNote],
  );

  return (
    <OrderMutationsProvider value={mutationHandlers}>
      <div className="tw:flex tw:flex-col tw:gap-8">
        {!!header && header}

        <OrderToolbar
          table={table}
          containerTypes={containerTypes}
          selectedContainerTypes={selectedContainerTypes}
          onContainerTypeChange={onContainerTypeChange}
          availableFilters={availableFilters}
          activeFilters={activeFilters}
          onFilterChange={onFilterChange}
          onFilterAdd={onFilterAdd}
          onFilterRemove={onFilterRemove}
          totalCount={totalCount}
          allSelected={allSelected}
          onSelectAll={handleSelectAll}
          primaryAction={primaryAction}
          overflowActions={overflowActions}
          firstRowFilterIds={firstRowFilterIds}
        />

        <Separator />

        {!isError && !isLoading && totalCount > 0 && (
          <div className="tw:text-sm tw:text-muted-foreground">
            {i18n.t('Order:resultCount', {
              shown: formatNumber(data.length),
              total: formatNumber(totalCount),
            })}
          </div>
        )}

        <div className="tw:rounded-md tw:border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isError ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="tw:h-24 tw:text-center tw:text-destructive"
                  >
                    {i18n.t('Common:error')}
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                Array.from({ length: 10 }).map((_, rowIndex) => (
                  <TableRow key={`loading-row-${rowIndex}`}>
                    {table.getVisibleLeafColumns().map((column) => (
                      <TableCell key={`${column.id}-loading-${rowIndex}`}>
                        <Skeleton className="tw:h-5 tw:w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const href = getRowHref?.(row.original);

                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const content = flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        );
                        const renderLink =
                          href != null &&
                          shouldRenderCellLink(cell.column.columnDef);

                        return (
                          <TableCell
                            key={cell.id}
                            className={renderLink ? 'tw:p-0' : undefined}
                          >
                            {renderLink && href ? (
                              <Link
                                to={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tw:block tw:h-full tw:w-full tw:cursor-pointer tw:p-2 tw:text-inherit tw:no-underline tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2 tw:focus-visible:outline-none tw:hover:text-inherit tw:hover:no-underline"
                              >
                                {content}
                              </Link>
                            ) : (
                              content
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="tw:h-24 tw:text-center tw:text-muted-foreground"
                  >
                    {i18n.t('Common:noResults')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <OrderPagination
          currentPage={currentPage}
          setCurrentPage={onPageChange}
          totalPages={pageCount}
        />
      </div>
    </OrderMutationsProvider>
  );
}
